import { useState, useEffect, useRef } from 'react';
import { 
  ShoppingBag, 
  LogOut, 
  Menu, 
  X, 
  Bell, 
  ChefHat, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  TrendingUp,
  Flame,
  Timer,
  Package,
  Trophy,
  Star,
  Search,
  Filter,
  ChevronRight,
  Info
} from 'lucide-react';
import type { User, Order } from '../../App';
import { getRestaurants } from '../../lib/mockData';
import { api } from '../../lib/api';
import { KitchenOrders } from '../orders/KitchenOrders';

interface StaffDashboardProps {
  user: User;
  onLogout: () => void;
}

export function StaffDashboard({ user, onLogout }: StaffDashboardProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeView, setActiveView] = useState<'all' | 'pending' | 'preparing'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showNotifications, setShowNotifications] = useState(false);
  const [focusedOrderId, setFocusedOrderId] = useState<string | null>(null);
  const orderCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [apiRestaurant, setApiRestaurant] = useState<any | null>(null);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [liveOrders, setLiveOrders] = useState<Order[]>([]);

  const restaurants = getRestaurants();
  const resolvedRestaurantId =
    user.restaurantId ||
    (user as any)?.restaurant_id ||
    (user as any)?.restaurant?.id ||
    restaurants[0]?.id;
  const localRestaurant = restaurants.find(r => r.id === resolvedRestaurantId);
  const restaurant: any =
    apiRestaurant ||
    localRestaurant ||
    (resolvedRestaurantId ? { id: resolvedRestaurantId, name: 'Restaurant' } : null);
  const restaurantLogo = String(restaurant?.logo || '').trim();
  const restaurantInitials = String(restaurant?.name || user?.name || 'Restaurant')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!focusedOrderId) return;
    const el = orderCardRefs.current[focusedOrderId];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timeout = setTimeout(() => setFocusedOrderId(null), 2500);
    return () => clearTimeout(timeout);
  }, [focusedOrderId, activeView, searchQuery]);

  useEffect(() => {
    let mounted = true;
    const loadRestaurant = async () => {
      if (!resolvedRestaurantId) return;
      try {
        const response = await api.restaurants.getById(String(resolvedRestaurantId));
        if (mounted && response?.data) {
          setApiRestaurant(response.data);
        }
      } catch {
        // Keep local/mock fallback.
      }
    };
    void loadRestaurant();
    return () => {
      mounted = false;
    };
  }, [resolvedRestaurantId]);

  useEffect(() => {
    setLogoLoadFailed(false);
  }, [restaurantLogo, restaurant?.id]);

  useEffect(() => {
    let mounted = true;

    const loadOrders = async () => {
      if (!resolvedRestaurantId) return;

      try {
        const response = await api.orders.getAll({ restaurantId: resolvedRestaurantId, limit: 100 } as any);
        const rawOrders = Array.isArray((response as any)?.data)
          ? (response as any).data
          : Array.isArray((response as any)?.data?.orders)
          ? (response as any).data.orders
          : [];

        if (!mounted) return;

        setLiveOrders(
          rawOrders.map((order: any) => ({
            id: String(order?.orderNumber || order?.id || ''),
            customerName: String(order?.customer?.name || order?.customerName || 'Customer'),
            createdAt: String(order?.createdAt || order?.placedAt || new Date().toISOString()),
            status: String(order?.status || 'PENDING').toLowerCase(),
            total: Number(order?.total || 0),
            items: Array.isArray(order?.items)
              ? order.items.map((item: any) => ({
                  name: String(item?.menuItem?.name || item?.name || 'Item'),
                  quantity: Number(item?.quantity || 0),
                  price: Number(item?.price || 0),
                }))
              : [],
            notes: '',
          }))
        );
      } catch {
        if (mounted) {
          setLiveOrders([]);
        }
      }
    };

    void loadOrders();
    const interval = setInterval(() => void loadOrders(), 15000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [resolvedRestaurantId]);

  const allOrders = liveOrders;

  const pendingOrders = allOrders.filter(o => o.status === 'pending' || o.status === 'confirmed');
  const preparingOrders = allOrders.filter(o => o.status === 'preparing');
  const readyOrders = allOrders.filter(o => o.status === 'ready');
  const completedToday = allOrders.filter(o => o.status === 'delivered').length;

  // Calculate stats
  const avgPrepTime = '18 min';
  const ordersCompletedToday = completedToday;
  const currentStreak = 12; // Demo value

  // Filter orders
  let displayOrders = allOrders;
  if (activeView === 'pending') {
    displayOrders = pendingOrders;
  } else if (activeView === 'preparing') {
    displayOrders = preparingOrders;
  }

  if (searchQuery) {
    displayOrders = displayOrders.filter(o => 
      o.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.customerName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  // Get order time elapsed
  const getTimeElapsed = (order: Order) => {
    const created = new Date(order.createdAt);
    const diff = Math.floor((currentTime.getTime() - created.getTime()) / 60000);
    return diff;
  };

  // Get order priority
  const getOrderPriority = (order: Order) => {
    const elapsed = getTimeElapsed(order);
    if (elapsed > 30) return 'high';
    if (elapsed > 20) return 'medium';
    return 'low';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-orange-600 to-orange-800 text-white transition-all duration-300 flex flex-col fixed h-full z-40 shadow-xl`}>
        <div className="p-4 border-b border-orange-500">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div>
                <h2 className="text-white">Kitchen Station</h2>
                <p className="text-orange-200 text-sm">{restaurant.name}</p>
              </div>
            )}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-orange-700 rounded-lg">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="flex-1 p-4 space-y-4">
          {/* Quick Stats */}
          {sidebarOpen && (
            <div className="space-y-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Trophy className="w-4 h-4 text-yellow-300" />
                  <span className="text-orange-200 text-xs">Today's Orders</span>
                </div>
                <p className="text-white font-semibold text-2xl">{ordersCompletedToday}</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Flame className="w-4 h-4 text-red-400" />
                  <span className="text-orange-200 text-xs">Current Streak</span>
                </div>
                <p className="text-white font-semibold">{currentStreak} orders</p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="space-y-2">
            <button
              onClick={() => setActiveView('all')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeView === 'all' ? 'bg-orange-700' : 'hover:bg-orange-700/50'
              }`}
            >
              <ShoppingBag className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>All Orders</span>}
            </button>

            <button
              onClick={() => setActiveView('pending')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative ${
                activeView === 'pending' ? 'bg-orange-700' : 'hover:bg-orange-700/50'
              }`}
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>Pending</span>}
              {pendingOrders.length > 0 && (
                <span className="absolute right-2 top-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingOrders.length}
                </span>
              )}
            </button>

            <button
              onClick={() => setActiveView('preparing')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative ${
                activeView === 'preparing' ? 'bg-orange-700' : 'hover:bg-orange-700/50'
              }`}
            >
              <ChefHat className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>Preparing</span>}
              {preparingOrders.length > 0 && (
                <span className="absolute right-2 top-2 w-6 h-6 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                  {preparingOrders.length}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="p-4 border-t border-orange-500">
          <div className="flex items-center gap-3 mb-3">
            {restaurantLogo && !logoLoadFailed ? (
              <img
                src={restaurantLogo}
                alt={restaurant?.name || user.name}
                className="w-10 h-10 rounded-full object-cover border-2 border-orange-400"
                onError={() => setLogoLoadFailed(true)}
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-orange-500 text-white border-2 border-orange-400 flex items-center justify-center font-semibold text-sm uppercase">
                {restaurantInitials || 'RA'}
              </div>
            )}
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate font-medium">{user.name}</p>
                <p className="text-orange-200 text-xs truncate">Kitchen Staff</p>
              </div>
            )}
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-2 bg-orange-700 hover:bg-orange-600 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
            <div>
              <h1 className="text-gray-900 mb-1">Kitchen Dashboard</h1>
              <p className="text-gray-600">Manage and prepare orders efficiently</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 w-64"
                />
              </div>
              <div className="relative">
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2 hover:bg-gray-100 rounded-lg relative"
                >
                  <Bell className="w-6 h-6 text-gray-600" />
                  {pendingOrders.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {pendingOrders.length}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-yellow-50">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-gray-900">Kitchen Notifications</h3>
                        <button
                          onClick={() => setShowNotifications(false)}
                          className="p-1 hover:bg-white rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5 text-gray-600" />
                        </button>
                      </div>
                      {(pendingOrders.length > 0 || preparingOrders.length > 0) && (
                        <p className="text-sm text-orange-600 mt-1 font-medium">
                          {pendingOrders.length} pending • {preparingOrders.length} preparing
                        </p>
                      )}
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                      {(pendingOrders.length > 0 || preparingOrders.length > 0) ? (
                        <div className="divide-y divide-gray-100">
                          {/* High Priority Orders First */}
                          {[...pendingOrders, ...preparingOrders]
                            .sort((a, b) => {
                              const priorityA = getOrderPriority(a);
                              const priorityB = getOrderPriority(b);
                              if (priorityA === 'high' && priorityB !== 'high') return -1;
                              if (priorityA !== 'high' && priorityB === 'high') return 1;
                              return 0;
                            })
                            .slice(0, 5)
                            .map((order) => {
                              const priority = getOrderPriority(order);
                              const elapsed = getTimeElapsed(order);
                              
                              return (
                                <button
                                  key={order.id}
                                  onClick={() => {
                                    setShowNotifications(false);
                                    if (order.status === 'pending' || order.status === 'confirmed') {
                                      setActiveView('pending');
                                    } else {
                                      setActiveView('preparing');
                                    }
                                    setSearchQuery(order.id);
                                    setFocusedOrderId(order.id);
                                  }}
                                  className="w-full p-4 hover:bg-gray-50 transition-colors text-left"
                                >
                                  <div className="flex items-start gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                      priority === 'high' 
                                        ? 'bg-red-100' 
                                        : order.status === 'preparing'
                                        ? 'bg-blue-100'
                                        : 'bg-orange-100'
                                    }`}>
                                      {priority === 'high' ? (
                                        <Flame className="w-5 h-5 text-red-600" />
                                      ) : order.status === 'preparing' ? (
                                        <ChefHat className="w-5 h-5 text-blue-600" />
                                      ) : (
                                        <AlertCircle className="w-5 h-5 text-orange-600" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <p className="font-semibold text-gray-900 text-sm">
                                          Order #{order.id}
                                        </p>
                                        {priority === 'high' && (
                                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                            URGENT
                                          </span>
                                        )}
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                          order.status === 'preparing'
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'bg-orange-100 text-orange-700'
                                        }`}>
                                          {order.status === 'preparing' ? 'Cooking' : 'Pending'}
                                        </span>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-1">
                                        {order.customerName} • {order.items.length} items
                                      </p>
                                      <div className="flex items-center gap-3 text-xs text-gray-500">
                                        <span className="flex items-center gap-1">
                                          <Timer className="w-3 h-3" />
                                          {elapsed} min ago
                                        </span>
                                        {priority === 'high' && (
                                          <span className="text-red-600 font-medium">Taking long!</span>
                                        )}
                                      </div>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                  </div>
                                </button>
                              );
                            })}
                        </div>
                      ) : (
                        <div className="p-8 text-center">
                          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                          </div>
                          <h4 className="text-gray-900 font-semibold mb-1">All Clear!</h4>
                          <p className="text-sm text-gray-600">No pending orders to prepare</p>
                        </div>
                      )}

                      {/* Quick Stats Footer */}
                      <div className="p-4 bg-gray-50 border-t border-gray-200">
                        <div className="grid grid-cols-3 gap-3 mb-3">
                          <div className="text-center">
                            <p className="text-xs text-gray-600 mb-1">Pending</p>
                            <p className="text-lg font-bold text-orange-600">{pendingOrders.length}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600 mb-1">Cooking</p>
                            <p className="text-lg font-bold text-blue-600">{preparingOrders.length}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600 mb-1">Ready</p>
                            <p className="text-lg font-bold text-green-600">{readyOrders.length}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setShowNotifications(false);
                            setActiveView('all');
                          }}
                          className="w-full text-center text-sm text-orange-600 hover:text-orange-700 font-medium"
                        >
                          View All Orders →
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <AlertCircle className="w-6 h-6" />
                </div>
                <div className="animate-pulse">
                  <span className="w-2 h-2 bg-white rounded-full inline-block"></span>
                </div>
              </div>
              <h3 className="text-white/80 text-sm mb-1">Pending Orders</h3>
              <p className="text-3xl font-bold">{pendingOrders.length}</p>
              <p className="text-white/60 text-xs mt-1">Needs attention</p>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <ChefHat className="w-6 h-6" />
                </div>
              </div>
              <h3 className="text-white/80 text-sm mb-1">In Preparation</h3>
              <p className="text-3xl font-bold">{preparingOrders.length}</p>
              <p className="text-white/60 text-xs mt-1">Active cooking</p>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <CheckCircle className="w-6 h-6" />
                </div>
              </div>
              <h3 className="text-white/80 text-sm mb-1">Ready to Serve</h3>
              <p className="text-3xl font-bold">{readyOrders.length}</p>
              <p className="text-white/60 text-xs mt-1">Awaiting pickup</p>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <Clock className="w-6 h-6" />
                </div>
              </div>
              <h3 className="text-white/80 text-sm mb-1">Avg Prep Time</h3>
              <p className="text-3xl font-bold">{avgPrepTime}</p>
              <p className="text-white/60 text-xs mt-1">Last 10 orders</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500 text-white rounded-lg">
                  <Star className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-gray-900 font-medium">Great job today, {user.name.split(' ')[0]}!</p>
                  <p className="text-gray-600 text-sm">You've completed {ordersCompletedToday} orders with excellent timing</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <KitchenOrders restaurantId={String(resolvedRestaurantId)} />
          </div>

          {false && (
            <>
            {/* Priority Orders Alert */}
            {displayOrders.filter(o => getOrderPriority(o) === 'high').length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <div>
                  <p className="text-red-900 font-medium">High Priority Orders</p>
                  <p className="text-red-700 text-sm">
                    {displayOrders.filter(o => getOrderPriority(o) === 'high').length} orders are taking longer than expected
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Orders Grid */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-gray-900">
                {activeView === 'all' ? 'All Orders' : activeView === 'pending' ? 'Pending Orders' : 'Preparing Orders'}
              </h2>
              <span className="text-gray-600 text-sm">{displayOrders.length} orders</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {displayOrders.map((order) => {
                const elapsed = getTimeElapsed(order);
                const priority = getOrderPriority(order);

                return (
                  <div
                    key={order.id}
                    ref={(el) => {
                      orderCardRefs.current[order.id] = el;
                    }}
                    className={`bg-white rounded-xl shadow-sm overflow-hidden border-l-4 hover:shadow-md transition-all ${
                      priority === 'high' ? 'border-red-500' :
                      priority === 'medium' ? 'border-yellow-500' :
                      'border-green-500'
                    } ${focusedOrderId === order.id ? 'ring-2 ring-orange-500 ring-offset-2' : ''}`}
                  >
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-gray-900 font-semibold mb-1">#{order.id}</h3>
                          <p className="text-gray-600 text-sm">{order.customerName}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                            order.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                            order.status === 'preparing' ? 'bg-purple-100 text-purple-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {order.status.replace('_', ' ')}
                          </div>
                          {priority === 'high' && (
                            <div className="p-1 bg-red-100 rounded-full">
                              <Flame className="w-4 h-4 text-red-600" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">{item.quantity}x {item.name}</span>
                            <span className="text-gray-500">₹{item.price}</span>
                          </div>
                        ))}
                      </div>

                      {order.notes && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-4">
                          <p className="text-yellow-900 text-xs">📝 {order.notes}</p>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                        <div className="flex items-center gap-1">
                          <Timer className="w-3 h-3" />
                          <span>{elapsed} min ago</span>
                        </div>
                        <span>₹{order.total}</span>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              order.status === 'pending' ? 'bg-orange-500 w-1/4' :
                              order.status === 'confirmed' ? 'bg-blue-500 w-1/2' :
                              order.status === 'preparing' ? 'bg-purple-500 w-3/4' :
                              'bg-green-500 w-full'
                            }`}
                          ></div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {order.status === 'pending' && (
                          <button className="flex-1 bg-orange-600 text-white py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium">
                            Start Preparing
                          </button>
                        )}
                        {order.status === 'preparing' && (
                          <button className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center justify-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Mark Ready
                          </button>
                        )}
                        {order.status === 'ready' && (
                          <div className="flex-1 bg-green-100 text-green-700 py-2 rounded-lg text-sm font-medium text-center flex items-center justify-center gap-2">
                            <Package className="w-4 h-4" />
                            Ready for Pickup
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {displayOrders.length === 0 && (
              <div className="text-center py-16 bg-white rounded-xl">
                <ChefHat className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-xl mb-2">No orders to display</p>
                <p className="text-gray-400">New orders will appear here</p>
              </div>
            )}
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
